import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEducationAuth } from '../../contexts/EducationAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LayoutDashboard, Wand2, BookOpen, Film, Share2, ShoppingCart,
  Plug, BarChart3, History, Shield, ChevronLeft, ChevronRight
} from 'lucide-react';

/**
 * AdminNav — Phase B · Workstream 6
 * Consolidated Super Admin navigation shell. Frontend-only.
 * Backend authorization remains authoritative on every route
 * (`super_admin: true` JWT claim + `require_shs_super_admin`).
 *
 * This is an ADDITIVE surface — every existing admin page is preserved,
 * every legacy URL still works. Nothing is renamed or moved.
 */
const NAV_ITEMS = [
  { key: 'home',      en: 'Home',                 es: 'Inicio',               icon: LayoutDashboard, href: '/admin/home' },
  { key: 'referrals', en: 'Referral requests',    es: 'Solicitudes',          icon: Shield,          href: '/admin/mysleep-referrals' },
  { key: 'labs',      en: 'Sleep centers',        es: 'Centros del sueño',    icon: ShoppingCart,    href: '/admin/affiliates' },
  { key: 'inst',      en: 'Institutional videos', es: 'Videos institucionales', icon: Film,          href: '/admin/institutional-videos' },
  { key: 'uploads',   en: 'Upload videos & photos', es: 'Subir videos y fotos', icon: Share2,        href: '/admin/uploads' },
  { key: 'education', en: 'Academy',              es: 'Academia',             icon: BookOpen,        href: '/admin/academy-overview' },
  { key: 'approvals', en: 'Pending approvals',    es: 'Aprobaciones',         icon: Shield,          href: '/admin/approvals' },
  { key: 'website',   en: 'Website sections',     es: 'Secciones del sitio',  icon: Wand2,           href: '/admin/website' },
  { key: 'members',   en: 'Memberships',          es: 'Membresías',           icon: BarChart3,       href: '/admin/memberships' },
  { key: 'activity',  en: 'Reports & activity',   es: 'Informes y actividad', icon: History,         href: '/admin/activity' },
  { key: 'advanced',  en: 'Advanced',             es: 'Avanzado',             icon: Plug,            href: '/admin/advanced' },
];

export default function AdminNav({ children, current }) {
  const navigate = useNavigate();
  const { user, isAdmin } = useEducationAuth();
  const { language } = useLanguage();
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Persist collapse state
    const stored = localStorage.getItem('shs_admin_nav_open');
    if (stored !== null) setOpen(stored === '1');
  }, []);

  useEffect(() => {
    localStorage.setItem('shs_admin_nav_open', open ? '1' : '0');
  }, [open]);

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-rose-600" data-testid="admin-nav-denied">
        Admin access required.
      </div>
    );
  }

  const items = NAV_ITEMS.map((it) => ({ ...it, label: language === 'es' ? it.es : it.en }));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex" data-testid="admin-nav-shell">
      {/* Desktop rail */}
      <aside
        className={`hidden md:flex flex-col border-r border-slate-800 bg-slate-900/60 transition-all duration-200 ${open ? 'w-60' : 'w-16'}`}
        data-testid="admin-nav-rail"
      >
        <div className="flex items-center justify-between px-3 py-4 border-b border-slate-800">
          {open && (
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-semibold tracking-wide">
                Super Admin
              </span>
            </div>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-1 rounded hover:bg-slate-800"
            data-testid="admin-nav-toggle-btn"
            aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
          >
            {open ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
        <nav className="flex-1 py-2">
          {items.map((it) => {
            const Icon = it.icon;
            const active = current === it.key;
            return (
              <button
                key={it.key}
                onClick={() => navigate(it.href)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                  active ? 'bg-cyan-500/10 text-cyan-300 border-l-2 border-cyan-400' : 'hover:bg-slate-800/70'
                }`}
                data-testid={`admin-nav-item-${it.key}`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                {open && <span className="flex-1 truncate">{it.label}</span>}
                {open && it.badge && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    {it.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-slate-800 px-3 py-3 text-[11px] text-slate-500">
          {open ? (
            <>
              <div className="text-slate-400 truncate">{user?.email}</div>
              <div className="mt-1">Founder</div>
            </>
          ) : (
            <div className="text-center">F</div>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed inset-x-0 top-0 z-40 bg-slate-950/95 border-b border-slate-800 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold">Super Admin</span>
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="px-3 py-1 text-xs rounded bg-slate-800"
          data-testid="admin-nav-mobile-toggle"
        >
          {mobileOpen ? 'Close' : 'Menu'}
        </button>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-slate-950/95 pt-12 px-3 pb-4 overflow-y-auto"
          data-testid="admin-nav-mobile-sheet"
        >
          <nav className="space-y-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.key}
                  onClick={() => { setMobileOpen(false); navigate(it.href); }}
                  className="w-full flex items-center gap-3 px-3 py-3 text-left text-sm rounded hover:bg-slate-800 min-h-[44px]"
                  data-testid={`admin-nav-mobile-item-${it.key}`}
                >
                  <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
                  <span className="flex-1">{it.label}</span>
                  {it.badge && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      {it.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-12 md:pt-0" data-testid="admin-nav-content">
        {children}
      </main>
    </div>
  );
}
